/**
 * Formatação PT-BR de entries de card_activity pra UI (FEAT-V2-002 PR 1).
 *
 * Dois helpers:
 *   - `formatActivityPhrase(activity, ctx)` — string descritiva ("moveu de X para Y")
 *   - `formatRelativeTime(iso)` — "há 2 min", "há 1 h", "ontem às 14:30"
 *   - `groupByDay(activities)` — agrupa por dia ("Hoje", "Ontem", "13 mai 2026")
 */

import type { CardActivity } from './card-timeline-store';
import { formatarData, formatTime } from './format-date';
import { hojeBR } from './datas-brasilia';

export interface ActivityFormatCtx {
  /** Resolver userId → nome (pra actor + assignee_changed.from/to). */
  resolveUser?: (id: string | null | undefined) => string;
  /** Resolver clienteId → nome (pra cliente em created/field_changed). */
  resolveClient?: (id: string | null | undefined) => string;
}

function formatValor(v: unknown): string {
  if (typeof v !== 'number') return String(v ?? '');
  // valor é em centavos (R3.5)
  const reais = v / 100;
  return reais.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatPrioridade(v: unknown): string {
  // Pós-Migration 033 (Bloco D.1): media→normal, critica→urgente.
  // Aliases legacy mantidos pra compat com activities históricas no banco
  // que foram gravadas antes da normalização e podem ainda aparecer no
  // log de atividades.
  const map: Record<string, string> = {
    baixa: 'Baixa',
    normal: 'Normal',
    alta: 'Alta',
    urgente: 'Urgente',
    // legacy — preserva renderização histórica
    media: 'Média',
    critica: 'Crítica',
  };
  if (typeof v !== 'string') return String(v ?? '');
  return map[v] ?? v;
}

/**
 * Constrói frase PT-BR pra um activity entry. Sem `ctx`, IDs aparecem
 * crus — caller passa resolveUser/resolveClient pra UX melhor.
 */
export function formatActivityPhrase(activity: CardActivity, ctx: ActivityFormatCtx = {}): string {
  const { tipo, payload } = activity;
  const u = ctx.resolveUser ?? ((id) => id ?? '?');
  const c = ctx.resolveClient ?? ((id) => id ?? '?');

  switch (tipo) {
    case 'created':
      return 'criou o card';
    case 'archived':
      return 'arquivou o card';
    case 'restored':
      return 'desarquivou o card';
    case 'deleted':
      return 'apagou o card';
    case 'stage_changed': {
      const fromLabel = (payload.fromLabel as string) ?? (payload.from as string) ?? '?';
      const toLabel = (payload.toLabel as string) ?? (payload.to as string) ?? '?';
      return `moveu de ${fromLabel} para ${toLabel}`;
    }
    case 'assignee_changed': {
      const from = u(payload.from as string | null);
      const to = u(payload.to as string | null);
      return `alterou responsável: ${from || '—'} → ${to || '—'}`;
    }
    case 'field_changed': {
      const field = (payload.field as string) ?? '?';
      let from = payload.from;
      let to = payload.to;
      if (field === 'valor') {
        from = formatValor(from);
        to = formatValor(to);
      } else if (field === 'prioridade') {
        from = formatPrioridade(from);
        to = formatPrioridade(to);
      } else if (field === 'cliente') {
        from = c(from as string);
        to = c(to as string);
      } else {
        from = String(from ?? '—');
        to = String(to ?? '—');
      }
      const label = FIELD_LABELS[field] ?? field;
      return `alterou ${label}: ${from} → ${to}`;
    }
    case 'comment_added':
      return 'comentou';
    case 'comment_edited':
      return 'editou um comentário';
    case 'comment_deleted':
      return 'removeu um comentário';
    default:
      return tipo;
  }
}

const FIELD_LABELS: Record<string, string> = {
  cliente: 'cliente',
  responsavelId: 'responsável',
  valor: 'valor',
  prioridade: 'prioridade',
  fase: 'fase',
  titulo: 'título',
  produto: 'produto',
  servico: 'serviço',
  observacoes: 'observações',
  origem: 'origem',
  proximaAcao: 'próxima ação',
  proximaAcaoDataIso: 'data da próxima ação',
};

const RTF = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' });

/**
 * "há 2 min" / "há 1 h" / "ontem às 14:30" / "13 mai 2026 às 10:00".
 *
 * - <60s: "agora"
 * - <60min: "há N min"
 * - <24h: "há N h"
 * - 24-48h: "ontem às HH:MM"
 * - >48h: "DD mmm YYYY às HH:MM"
 */
export function formatRelativeTime(iso: string, nowMs = Date.now()): string {
  const t = Date.parse(iso + (iso.endsWith('Z') ? '' : 'Z'));
  if (!Number.isFinite(t)) return iso;
  const diffMs = nowMs - t;
  const secs = Math.round(diffMs / 1000);
  if (secs < 60) return 'agora';
  const mins = Math.round(secs / 60);
  if (mins < 60) return RTF.format(-mins, 'minute');
  const hours = Math.round(mins / 60);
  if (hours < 24) return RTF.format(-hours, 'hour');
  // Comparar por dia calendário DE BRASÍLIA (não 24h exatas, e não o dia do computador — #484)
  const date = new Date(t);
  const now = new Date(nowMs);
  const daysDiff = Math.round(
    (Date.parse(`${hojeBR(now)}T00:00:00Z`) - Date.parse(`${hojeBR(date)}T00:00:00Z`)) / (24 * 60 * 60 * 1000),
  );
  const hhmm = formatTime(date);
  if (daysDiff === 1) return `ontem às ${hhmm}`;
  const dateStr = formatarData(date, { day: '2-digit', month: 'short', year: 'numeric' });
  return `${dateStr} às ${hhmm}`;
}

/**
 * Agrupa activities por dia calendário ("Hoje" / "Ontem" / "13 mai 2026").
 * Retorna array ordenado mantendo ordem de entrada (que é DESC do backend).
 */
export function groupByDay<T extends { criadoEmIso: string }>(
  items: T[],
  nowMs = Date.now()
): Array<{ label: string; items: T[] }> {
  const groups = new Map<string, T[]>();
  const order: string[] = [];
  const now = new Date(nowMs);
  const todayKey = formatDayKey(now);
  const yesterdayKey = formatDayKey(new Date(nowMs - 24 * 60 * 60 * 1000));

  for (const it of items) {
    const t = Date.parse(it.criadoEmIso + (it.criadoEmIso.endsWith('Z') ? '' : 'Z'));
    const d = Number.isFinite(t) ? new Date(t) : new Date();
    const key = formatDayKey(d);
    let label: string;
    if (key === todayKey) label = 'Hoje';
    else if (key === yesterdayKey) label = 'Ontem';
    else label = formatarData(d, { day: '2-digit', month: 'long', year: 'numeric' });
    if (!groups.has(label)) {
      groups.set(label, []);
      order.push(label);
    }
    groups.get(label)!.push(it);
  }

  return order.map((label) => ({ label, items: groups.get(label)! }));
}

/** O dia de Brasília do instante (#484) — não o dia do computador de quem abre. */
function formatDayKey(d: Date): string {
  return hojeBR(d);
}
